import React, { useEffect, useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    CircularProgress,
    Alert,
    MenuItem,
    Select,
    InputLabel,
    FormControl,
    IconButton,
    Box,
    Chip,
    Grid,
    Typography,
    OutlinedInput
} from '@mui/material';
import { CloseOutlined, CancelOutlined, CheckCircleOutline } from '@mui/icons-material';
import { i18next } from '@/config/i18n';
import { makeStyles } from '@mui/styles';
import { VehicleSettingsResponse, VehicleRoutingType, VEHICLE_ROUTING_TYPES } from '@/config/types/Vehicles';
import TimeField from '../VehicleEditor/TimeField';
import { LoadClass } from '@/config/types/LoadClass';
import { useId } from 'react';
import { useSettings } from '../Settings/SettingsContext';

export type Props = {
    open: boolean;
    settings: VehicleSettingsResponse | null;
    loading: boolean;
    error: string | null;
    onClose: () => void;
    onSave: (updated: VehicleSettingsResponse) => Promise<void>;
    loadClasses: LoadClass[];
    loadingClasses: boolean;
};

const useStyles = makeStyles({
    dialogTitle: {
        padding: '14px',
        marginBottom: '10px',
        background: 'rgba(239, 89, 39, 0.12)'
    },
    generalTitle: {
        fontSize: '17px',
        fontWeight: '500'
    },
    headerTitle: {
        background: '#dfdede',
        padding: '10px',
        fontSize: '14px'
    }

});

const VehicleSettings: React.FC<Props> = ({ open, settings, loading, error, onClose, onSave, loadClasses, loadingClasses }) => {
    const [form, setForm] = useState<VehicleSettingsResponse | null>(null);
    const classes = useStyles();
    const labelId = useId();
    const { settings: appSettings } = useSettings();
    const dimensions = appSettings.load_dimensions || [];

    useEffect(() => {
        setForm(settings);
    }, [settings]);

    const updateField = useCallback(<K extends keyof VehicleSettingsResponse>(
        key: K,
        value: VehicleSettingsResponse[K]
    ) => {
        setForm(prev => (prev ? { ...prev, [key]: value } : prev));
    }, []);

    const handleNumberChange = useCallback(
        (key: keyof VehicleSettingsResponse) =>
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const n = Number(e.target.value);
                const clamped = Number.isNaN(n) ? 0 : Math.max(0, Math.floor(n));
                updateField(key, clamped as any);
            },
        [updateField]
    );

    const features = [
        {
            key: 'round_trip' as const,
            labelKey: 'common:vehicleEditor.roundtrip'
        }
    ];

    return (
        <Dialog open={open}
            onClose={onClose}
            fullWidth maxWidth="sm"
            data-testid="vehicle-settings-dialog">
            <DialogTitle className={classes.dialogTitle} data-testid="vehicle-settings-title">
                <Typography className={classes.generalTitle} variant="subtitle1" component="div">
                    {i18next.t('common:vehicleSetting.generaltitle')}
                </Typography>
                <IconButton
                    aria-label="close"
                    data-testid="settings-close-btn"
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 8, right: 12, p: 0.5 }}>
                    <CloseOutlined />
                </IconButton>
            </DialogTitle>

            <DialogContent
                data-testid="vehicle-settings-content"
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {loading && <CircularProgress />}
                {error && <Alert severity="error">{error}</Alert>}

                {form && (
                    <>
                        <Box pt={1}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth data-testid="vehicle-type-formcontrol">
                                        <InputLabel id="vehicle-type-label">
                                            {i18next.t('common:vehicleSetting.vehicleRoutingType')}
                                        </InputLabel>
                                        <Select
                                            labelId="vehicle-type-label"
                                            size="small"
                                            value={form.vehicle_routing_type}
                                            label={i18next.t('common:vehicleSetting.vehicleRoutingType')}
                                            data-testid="vehicle-type-select"
                                            onChange={e => updateField('vehicle_routing_type', e.target.value as VehicleRoutingType)}
                                            sx={{
                                                fontSize: '14px',
                                                '& .MuiSelect-select': {
                                                    fontSize: '14px',
                                                },
                                            }}
                                        >
                                            {VEHICLE_ROUTING_TYPES.map(type => (
                                                <MenuItem key={type} value={type} data-testid={`vehicle-type-option-${type}`}>
                                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        fullWidth
                                        disabled={loadingClasses}
                                        variant="outlined"
                                        size="small"
                                    >
                                        <InputLabel
                                            id={labelId}
                                            shrink
                                            sx={{
                                                left: '0px',
                                                '&.MuiInputLabel-shrink': {
                                                    transform: 'translate(10px, -8px) scale(0.75)',
                                                },
                                            }}
                                        >
                                            {i18next.t('common:vehicleTable.loadClassName')}
                                        </InputLabel>

                                        <Select
                                            labelId={labelId}
                                            value={form.load_class_name || ''}
                                            onChange={(e) => updateField('load_class_name', e.target.value || null)}
                                            data-testid="load-class-select"
                                            sx={{
                                                fontSize: '14px',
                                                '& .MuiSelect-select': {
                                                    fontSize: '14px',
                                                },
                                            }}
                                            input={
                                                <OutlinedInput
                                                    label={i18next.t('common:vehicleTable.loadClassName')}
                                                    notched
                                                />
                                            }
                                        >
                                            {loadClasses.map((lc) => (
                                                <MenuItem
                                                    key={lc.id}
                                                    value={lc.name}
                                                    data-testid={`load-class-option-${lc.name}`}
                                                >
                                                    {lc.name}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        {loadingClasses && (
                                            <CircularProgress
                                                size={20}
                                                sx={{ mt: 1 }}
                                                data-testid="load-class-loading"
                                            />
                                        )}
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Ausstattung */}
                        <Typography variant="subtitle2" className={classes.headerTitle} data-testid="section-general">
                            {i18next.t('common:vehicleSetting.General')}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                            {features.map(f => {
                                const active = Boolean(form[f.key]);
                                const chipLabel = active
                                    ? i18next.t('common:vehicleEditor.withFeature', {
                                        feature: i18next.t(f.labelKey)
                                    })
                                    : i18next.t('common:vehicleEditor.withoutFeature', {
                                        feature: i18next.t(f.labelKey)
                                    });

                                return (
                                    <Chip
                                        key={f.key}
                                        icon={active ? <CheckCircleOutline /> : <CancelOutlined />}
                                        label={chipLabel}
                                        size="small"
                                        clickable
                                        aria-pressed={active}
                                        color={active ? 'success' : 'error'}
                                        variant={active ? 'filled' : 'outlined'}
                                        data-testid={`feature-${f.key}`}
                                        onClick={() => updateField(f.key, !active as any)}
                                        sx={{
                                            fontSize: '12px',
                                        }}
                                    />
                                );
                            })}
                        </Box>

                        {/* Zeiten */}
                        <Typography variant="subtitle2" className={classes.headerTitle} data-testid="section-times">
                            {i18next.t('common:export.times')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TimeField
                                label={i18next.t('common:vehicleTable.departureWindowStart')}
                                valueStr={form.operation_start_window?.start}
                                size="small"
                                onChange={time =>
                                    updateField('operation_start_window', { start: time, end: form.operation_start_window?.end ?? null } as any)
                                }
                                data-testid="time-departure-start"
                            />
                            <TimeField
                                label={i18next.t('common:vehicleTable.departureWindowEnd')}
                                valueStr={form.operation_start_window?.end}
                                size="small"
                                onChange={time =>
                                    updateField('operation_start_window', { start: form.operation_start_window?.start ?? null, end: time } as any)
                                }
                                data-testid="time-departure-end"
                            />
                            <TimeField
                                label={i18next.t('common:vehicleTable.returnTimeTo')}
                                size="small"
                                valueStr={form.operation_end}
                                onChange={time => setForm(prev => prev && ({ ...prev, operation_end: time }))}
                                data-testid="time-return-to"
                            />
                        </Box>

                        <Box display="flex" gap={2}>
                            {/* max_working_time_min — existing custom logic kept as-is, just input guards */}
                            <TextField
                                label={i18next.t('common:vehicleTable.maxWorkingTime')}
                                type="number"
                                size="small"
                                fullWidth
                                value={form.max_working_time_min ?? ''}
                                InputLabelProps={{ shrink: true }}
                                sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                                onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === '') {
                                        setForm(f => f && ({ ...f, max_working_time_min: null }));
                                        return;
                                    }
                                    const n = Number(raw);
                                    const clamped = Number.isNaN(n) ? 0 : Math.max(0, Math.floor(n));
                                    setForm(f => f && ({ ...f, max_working_time_min: clamped }));
                                }}
                                onBlur={(e) => {
                                    const n = Number(e.target.value);
                                    if (!Number.isNaN(n) && n < 0) {
                                        setForm(f => f && ({ ...f, max_working_time_min: 0 }));
                                    }
                                }}
                                data-testid="input-max-working-time-min"
                            />
                        </Box>

                        {/* Kosten */}
                        <Typography variant="subtitle2" className={classes.headerTitle} data-testid="section-costs">
                            {i18next.t('common:vehicleEditor.costIn')}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label={i18next.t('common:vehicleTable.fixedCostEur')}
                                type="number"
                                size="small"
                                fullWidth
                                value={form.fixed_cost_eur ?? ''}
                                onChange={handleNumberChange('fixed_cost_eur')}
                                InputLabelProps={{ shrink: true }}
                                data-testid="input-fixed-cost-eur"
                                sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                            />
                            <TextField
                                label={i18next.t('common:vehicleTable.variableCost')}
                                type="number"
                                size="small"
                                fullWidth
                                value={form.variable_cost_eur_per_km ?? ''}
                                onChange={handleNumberChange('variable_cost_eur_per_km')}
                                InputLabelProps={{ shrink: true }}
                                data-testid="input-variable-cost-eur-per-km"
                                sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                            />
                        </Box>

                        {/* Stops service time */}
                        <Typography variant="subtitle2" className={classes.headerTitle} data-testid="section-stop-services">
                            {i18next.t('common:vehicleTable.stopServiceTime')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={i18next.t('common:vehicleTable.stopFixedServiceTime')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={form.fixed_service_time_s ?? ''}
                                    onChange={handleNumberChange('fixed_service_time_s')}
                                    InputLabelProps={{ shrink: true }}
                                    data-testid="input-fixed-service-time-s"
                                    sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                    inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                                />
                            </Grid>
                            {dimensions.map((dim, index) => (
                                <Grid item xs={12} sm={4} key={`stop-var-${index}`}>
                                    <TextField
                                        label={i18next.t('common:vehicleTable.stopServiceLabel', { n: index + 1 })}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={form.variable_service_time_s_per_load?.[index] ?? ''}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                        onChange={e => {
                                            const currentList = form.variable_service_time_s_per_load || [];
                                            const updated = dimensions.map((_, i) => currentList[i] ?? 0);
                                            const n = Number(e.target.value);
                                            updated[index] = Number.isNaN(n) ? 0 : Math.max(0, Math.floor(n));
                                            updateField('variable_service_time_s_per_load', updated as any);
                                        }}
                                        data-testid={`input-stop-var-s-per-load-${index}`}
                                        inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                        onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Loading Service time */}
                        <Typography variant="subtitle2" className={classes.headerTitle} data-testid="section-load-services">
                            {i18next.t('common:vehicleTable.loadServiceTime')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={i18next.t('common:vehicleTable.loadingFixedServiceTime')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={form.depot_service_time_config?.loading_service_time_config?.fixed_service_time_s ?? ''}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                    onChange={e => {
                                        const n = Number(e.target.value);
                                        const clamped = Number.isNaN(n) ? 0 : Math.max(0, Math.floor(n));
                                        setForm((prev: any) => prev ? {
                                            ...prev,
                                            depot_service_time_config: {
                                                ...prev.depot_service_time_config,
                                                loading_service_time_config: {
                                                    ...prev.depot_service_time_config?.loading_service_time_config,
                                                    fixed_service_time_s: clamped
                                                }
                                            }
                                        } : prev);
                                    }}
                                    data-testid="input-depot-loading-fixed-service-time-s"
                                    inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                                />
                            </Grid>
                            {dimensions.map((dim, index) => (
                                <Grid item xs={12} sm={4} key={`load-var-${index}`}>
                                    <TextField
                                        label={i18next.t(`common:vehicleTable.loadingServiceLabel${index + 1}`)}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={form.depot_service_time_config?.loading_service_time_config?.variable_service_time_s_per_load?.[index] ?? ''}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                        onChange={e => {
                                            const currentList = form.depot_service_time_config?.loading_service_time_config?.variable_service_time_s_per_load || [];
                                            const updated = dimensions.map((_, i) => currentList[i] ?? 0);
                                            const n = Number(e.target.value);
                                            updated[index] = Number.isNaN(n) ? 0 : Math.max(0, Math.floor(n));
                                            setForm((prev: any) => prev ? {
                                                ...prev,
                                                depot_service_time_config: {
                                                    ...prev.depot_service_time_config,
                                                    loading_service_time_config: {
                                                        ...prev.depot_service_time_config?.loading_service_time_config,
                                                        variable_service_time_s_per_load: updated
                                                    }
                                                }
                                            } : prev);
                                        }}
                                        data-testid={`input-load-var-s-per-load-${index}`}
                                        inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                        onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                                    />
                                </Grid>
                            ))}
                        </Grid>

                        {/* Unloading Service time */}
                        <Typography variant="subtitle2" className={classes.headerTitle} data-testid="section-unload-services">
                            {i18next.t('common:vehicleTable.unloadServiceTime')}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    label={i18next.t('common:vehicleTable.unloadingFixedServiceTime')}
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={form.depot_service_time_config?.unloading_service_time_config?.fixed_service_time_s ?? ''}
                                    InputLabelProps={{ shrink: true }}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                    onChange={e => {
                                        const n = Number(e.target.value);
                                        const clamped = Number.isNaN(n) ? 0 : Math.max(0, Math.floor(n));
                                        setForm((prev: any) => prev ? {
                                            ...prev,
                                            depot_service_time_config: {
                                                ...prev.depot_service_time_config,
                                                unloading_service_time_config: {
                                                    ...prev.depot_service_time_config?.unloading_service_time_config,
                                                    fixed_service_time_s: clamped
                                                }
                                            }
                                        } : prev);
                                    }}
                                    data-testid="input-depot-unloading-fixed-service-time-s"
                                    inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                    onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                                />
                            </Grid>
                            {dimensions.map((dim, index) => (
                                <Grid item xs={12} sm={4} key={`unload-var-${index}`}>
                                    <TextField
                                        label={i18next.t(`common:vehicleTable.unloadingServiceLabel${index + 1}`)}
                                        type="number"
                                        size="small"
                                        fullWidth
                                        value={form.depot_service_time_config?.unloading_service_time_config?.variable_service_time_s_per_load?.[index] ?? ''}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiInputBase-input': { fontSize: '14px' } }}
                                        onChange={e => {
                                            const currentList = form.depot_service_time_config?.unloading_service_time_config?.variable_service_time_s_per_load || [];
                                            const updated = dimensions.map((_, i) => currentList[i] ?? 0);
                                            const n = Number(e.target.value);
                                            updated[index] = Number.isNaN(n) ? 0 : Math.max(0, Math.floor(n));
                                            setForm((prev: any) => prev ? {
                                                ...prev,
                                                depot_service_time_config: {
                                                    ...prev.depot_service_time_config,
                                                    unloading_service_time_config: {
                                                        ...prev.depot_service_time_config?.unloading_service_time_config,
                                                        variable_service_time_s_per_load: updated
                                                    }
                                                }
                                            } : prev);
                                        }}
                                        data-testid={`input-unload-var-s-per-load-${index}`}
                                        inputProps={{ min: 0, step: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
                                        onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                        onKeyDown={(e) => { if (['e', 'E', '+', '-', '.'].includes(e.key)) e.preventDefault(); }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    onClick={() => form && onSave(form)}
                    disabled={!form || loading || Boolean(error)}
                    data-testid="vehicle-settings-save"
                >
                    {i18next.t('common:settings.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default VehicleSettings;
